import React from 'react';

interface LLMPipeConfirmStepProps {
  provider: string;
  name: string;
  description: string;
  model: string;
  endpoint: string;
  secretId: string;
  azureApiVersion?: string;
  region?: string;
  requestsPerMinute: number;
  tokensPerMinute: number;
  enableCostTracking: boolean;
  inputCostPer1K: number;
  outputCostPer1K: number;
  monthlyBudget: number;
  onBack: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const LLMPipeConfirmStep: React.FC<LLMPipeConfirmStepProps> = ({
  provider,
  name,
  description,
  model,
  endpoint,
  secretId,
  azureApiVersion,
  region,
  requestsPerMinute,
  tokensPerMinute,
  enableCostTracking,
  inputCostPer1K,
  outputCostPer1K,
  monthlyBudget,
  onBack,
  onConfirm,
  onCancel
}) => {
  const getProviderName = () => {
    const names: Record<string, string> = {
      'openai': 'OpenAI',
      'anthropic': 'Anthropic Claude',
      'bedrock': 'AWS Bedrock',
      'azure': 'Azure OpenAI',
      'gemini': 'Google Gemini'
    };
    return names[provider] || provider;
  };

  const getProviderIcon = () => {
    const icons: Record<string, string> = {
      'openai': 'fa-brain',
      'anthropic': 'fa-robot',
      'bedrock': 'fa-aws',
      'azure': 'fa-cloud',
      'gemini': 'fa-gem'
    };
    return icons[provider] || 'fa-plug';
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <h4 className="mb-3">
          <i className="fas fa-check-circle mr-2"></i>
          Confirm LLM Pipe Configuration
        </h4>

        <p className="text-muted mb-4">
          Review your LLM pipe configuration before creating it. You can go back to make changes if needed.
        </p>

        {/* Provider Info */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className={`fas ${getProviderIcon()} mr-2`}></i>
              Provider Information
            </h6>
          </div>
          <div className="card-body" style={{ backgroundColor: 'transparent' }}>
            <div className="row">
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="text-muted small mb-1">Provider</div>
                <div className="font-weight-bold">{getProviderName()}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small mb-1">Model</div>
                <div className="font-weight-bold"><code>{model}</code></div>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-info-circle mr-2"></i>
              Basic Information
            </h6>
          </div>
          <div className="card-body" style={{ backgroundColor: 'transparent' }}>
            <div className="mb-3">
              <div className="text-muted small mb-1">Name</div>
              <div className="font-weight-bold">{name}</div>
            </div>
            {description && (
              <div className="mb-0">
                <div className="text-muted small mb-1">Description</div>
                <div>{description}</div>
              </div>
            )}
          </div>
        </div>

        {/* Connection Details */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-plug mr-2"></i>
              Connection Details
            </h6>
          </div>
          <div className="card-body" style={{ backgroundColor: 'transparent' }}>
            <div className="mb-3">
              <div className="text-muted small mb-1">Endpoint</div>
              <div className="font-weight-bold">
                <code className="small">{endpoint}</code>
              </div>
            </div>
            <div className="mb-3">
              <div className="text-muted small mb-1">Authentication</div>
              <div>
                <i className="fas fa-key mr-1"></i>
                Secret: <code>{secretId}</code>
              </div>
            </div>
            {azureApiVersion && (
              <div className="mb-3">
                <div className="text-muted small mb-1">API Version</div>
                <div className="font-weight-bold"><code>{azureApiVersion}</code></div>
              </div>
            )}
            {region && (
              <div className="mb-0">
                <div className="text-muted small mb-1">Region</div>
                <div className="font-weight-bold">{region}</div>
              </div>
            )}
          </div>
        </div>

        {/* Rate Limiting */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-tachometer-alt mr-2"></i>
              Rate Limiting
            </h6>
          </div>
          <div className="card-body" style={{ backgroundColor: 'transparent' }}>
            {requestsPerMinute > 0 || tokensPerMinute > 0 ? (
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <div className="text-muted small mb-1">Requests Per Minute</div>
                  <div className="font-weight-bold">
                    {requestsPerMinute.toLocaleString()}
                  </div>
                  <div className="text-muted small">
                    ~{(requestsPerMinute / 60).toFixed(1)} req/sec
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small mb-1">Tokens Per Minute</div>
                  <div className="font-weight-bold">
                    {tokensPerMinute.toLocaleString()}
                  </div>
                  <div className="text-muted small">
                    ~{Math.round(tokensPerMinute / 60).toLocaleString()} tok/sec
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-muted">
                <i className="fas fa-infinity mr-2"></i>
                No rate limiting configured
              </div>
            )}
          </div>
        </div>

        {/* Cost Tracking */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-dollar-sign mr-2"></i>
              Cost Tracking
            </h6>
          </div>
          <div className="card-body" style={{ backgroundColor: 'transparent' }}>
            {enableCostTracking ? (
              <>
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div className="text-muted small mb-1">Input Cost (per 1K tokens)</div>
                    <div className="font-weight-bold">
                      ${inputCostPer1K.toFixed(4)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small mb-1">Output Cost (per 1K tokens)</div>
                    <div className="font-weight-bold">
                      ${outputCostPer1K.toFixed(4)}
                    </div>
                  </div>
                </div>
                {monthlyBudget > 0 && (
                  <div>
                    <div className="text-muted small mb-1">Monthly Budget Limit</div>
                    <div className="font-weight-bold">
                      ${monthlyBudget.toFixed(2)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted">
                <i className="fas fa-times mr-2"></i>
                Cost tracking disabled
              </div>
            )}
          </div>
        </div>

        <div className="alert alert-info">
          <i className="fas fa-info-circle mr-2"></i>
          <strong>Ready to Create:</strong> Once you click "Create Pipe", your LLM pipe will be configured
          and ready to use. You can modify these settings later from the pipe's configuration page.
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back
          </button>
          <button
            type="button"
            className="btn btn-success btn-lg"
            onClick={onConfirm}
          >
            <i className="fas fa-check mr-1"></i>
            Create Pipe
          </button>
        </div>
      </div>
    </div>
  );
};

export default LLMPipeConfirmStep;
